"use client";

import { type ReactNode, useCallback, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useMediaQuery } from "../../hooks/useMediaQuery";
import { Button } from "../Button";
import { Icon } from "../Icon";
import { TextField } from "../TextField";
import { Tray } from "../Tray";

export interface GlobalSearchProps {
  /**
   * Placeholder text for the search input
   * @default "Search..."
   */
  placeholder?: string;
  /**
   * Value of the search input (controlled)
   */
  value?: string;
  /**
   * Handler for when the search input changes
   */
  onChange?: (value: string) => void;
  /**
   * Whether the search is open (controlled)
   */
  isOpen?: boolean;
  /**
   * Handler for when the search is opened/closed
   */
  onOpenChange?: (isOpen: boolean) => void;
  /**
   * Content to display in the results area
   */
  children: ReactNode;
  /**
   * Maximum height for the popover results container (desktop only)
   * @default "400px"
   */
  maxHeight?: string;
  /**
   * Visual variant for the search trigger
   * - `default`: Standard input styling
   * - `elevated`: Premium styling for dashboard global search
   * @default "default"
   */
  variant?: "default" | "elevated";
  /**
   * Additional CSS classes
   */
  className?: string;
}

/**
 * GlobalSearch
 *
 * Complete global search experience with trigger + results.
 * Desktop: SearchTrigger with popover below
 * Mobile: Icon trigger with full-screen tray
 *
 * Handles CMD+K shortcuts automatically.
 *
 * @example
 * ```tsx
 * const [query, setQuery] = useState("");
 * const [isOpen, setIsOpen] = useState(false);
 *
 * <GlobalSearch
 *   value={query}
 *   onChange={setQuery}
 *   isOpen={isOpen}
 *   onOpenChange={setIsOpen}
 *   placeholder="Search sites, devices, contacts..."
 * >
 *   <SearchResultsList>
 *     <SearchResultGroup title="Sites" count={2}>
 *       <SearchResultItem id="1" title="HQ" />
 *     </SearchResultGroup>
 *   </SearchResultsList>
 * </GlobalSearch>
 * ```
 */
export function GlobalSearch({
  placeholder = "Search...",
  value,
  onChange,
  isOpen: controlledIsOpen,
  onOpenChange,
  children,
  maxHeight = "400px",
  variant = "default",
  className,
}: GlobalSearchProps) {
  const [uncontrolledIsOpen, setUncontrolledIsOpen] = useState(false);
  const isMobile = useMediaQuery("(max-width: 767px)");
  const containerRef = useRef<HTMLDivElement>(null);
  const popoverRef = useRef<HTMLDivElement>(null);

  const isOpen = controlledIsOpen !== undefined ? controlledIsOpen : uncontrolledIsOpen;
  const setIsOpen = useCallback(
    (open: boolean) => {
      if (onOpenChange) {
        onOpenChange(open);
      } else {
        setUncontrolledIsOpen(open);
      }
    },
    [onOpenChange]
  );

  // Handle CMD/CTRL+K shortcut
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === "k") {
        e.preventDefault();
        setIsOpen(!isOpen);
      }
      if (e.key === "Escape" && isOpen) {
        setIsOpen(false);
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [isOpen, setIsOpen]);

  // Focus input when opened (desktop only)
  useEffect(() => {
    if (isOpen && !isMobile && containerRef.current) {
      // Use setTimeout to ensure the input is rendered and focusable
      setTimeout(() => {
        const input = containerRef.current?.querySelector<HTMLInputElement>("input");
        input?.focus();
      }, 0);
    }
  }, [isOpen, isMobile]);

  // Click outside to close (desktop only)
  useEffect(() => {
    if (!isOpen || isMobile) return;

    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node;
      const clickedInsideTrigger = containerRef.current?.contains(target);
      const clickedInsidePopover = popoverRef.current?.contains(target);

      if (!clickedInsideTrigger && !clickedInsidePopover) {
        setIsOpen(false);
      }
    };

    // Use capture phase to ensure we catch the event before other handlers
    document.addEventListener("mousedown", handleClickOutside, true);
    return () => document.removeEventListener("mousedown", handleClickOutside, true);
  }, [isOpen, isMobile, setIsOpen]);

  const handleInputChange = (newValue: string) => {
    onChange?.(newValue);
  };

  const handleCloseTray = useCallback(() => {
    setIsOpen(false);
  }, [setIsOpen]);

  const handleClearInput = useCallback(() => {
    onChange?.("");
  }, [onChange]);

  // Track popover position (must be before conditional return to avoid hooks violation)
  const [popoverPosition, setPopoverPosition] = useState<{
    top: number;
    left: number;
    width: number;
  } | null>(null);

  // Update popover position when opened (with throttling for performance)
  useEffect(() => {
    if (isOpen && !isMobile && containerRef.current) {
      let rafId: number | null = null;

      const updatePosition = () => {
        const rect = containerRef.current?.getBoundingClientRect();
        if (rect) {
          setPopoverPosition({
            top: rect.bottom + 8, // 8px gap (mt-2)
            left: rect.left,
            width: rect.width,
          });
        }
      };

      const throttledUpdate = () => {
        if (rafId === null) {
          rafId = requestAnimationFrame(() => {
            updatePosition();
            rafId = null;
          });
        }
      };

      updatePosition();
      window.addEventListener("resize", throttledUpdate);
      window.addEventListener("scroll", throttledUpdate, true);
      return () => {
        window.removeEventListener("resize", throttledUpdate);
        window.removeEventListener("scroll", throttledUpdate, true);
        if (rafId !== null) cancelAnimationFrame(rafId);
      };
    }
  }, [isOpen, isMobile]);

  const handleOpenSearch = useCallback(() => {
    setIsOpen(true);
  }, [setIsOpen]);

  // Mobile: Icon trigger + Full-screen tray
  if (isMobile) {
    return (
      <div key="mobile" className={className}>
        <button
          type="button"
          onClick={handleOpenSearch}
          className="flex items-center justify-center w-10 h-10 rounded-lg hover:bg-background-hover transition-colors"
          aria-label="Open search"
        >
          <Icon name="MagnifyingGlass" size="md" />
        </button>

        <Tray isOpen={isOpen} onClose={handleCloseTray} contentPadding={false} height="80vh">
          <div className="flex items-center gap-2 px-4 pt-4">
            <div className="flex-1">
              <TextField
                value={value}
                onChange={handleInputChange}
                placeholder={placeholder}
                showSearchIcon={true}
                isClearable={!!value}
                onClear={handleClearInput}
                reserveErrorSpace={false}
                autoFocus
              />
            </div>
            <Button variant="icon" onPress={handleCloseTray} aria-label="Close search" className="flex-shrink-0">
              <Icon name="X" size="md" />
            </Button>
          </div>
          <div className="mt-4">{children}</div>
        </Tray>
      </div>
    );
  }

  // Handle keyboard navigation from input to first result
  const handleInputKeyDown = (e: React.KeyboardEvent) => {
    // Only intercept if the currently focused element is the input, not a result item
    const target = e.target as HTMLElement;
    const isInputFocused = target.tagName === "INPUT";

    if (e.key === "ArrowDown" && isOpen && isInputFocused) {
      e.preventDefault();
      // Move focus to first search result - query from popover since it's portaled
      const firstResult = popoverRef.current?.querySelector<HTMLElement>("[data-search-result]");
      if (firstResult) {
        firstResult.focus();
      }
    }
  };

  // Handle keyboard navigation within results
  const handlePopoverKeyDown = (e: React.KeyboardEvent) => {
    const target = e.target as HTMLElement;
    const hasDataAttribute = target.hasAttribute("data-search-result");

    if (!hasDataAttribute) return;

    const allResults = Array.from(popoverRef.current?.querySelectorAll<HTMLElement>("[data-search-result]") || []);
    const currentIndex = allResults.indexOf(target);

    if (e.key === "ArrowDown") {
      e.preventDefault();
      e.stopPropagation();
      const nextIndex = Math.min(currentIndex + 1, allResults.length - 1);
      allResults[nextIndex]?.focus();
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      e.stopPropagation();
      if (currentIndex === 0) {
        // Return focus to input
        containerRef.current?.querySelector<HTMLInputElement>("input")?.focus();
      } else {
        const prevIndex = Math.max(currentIndex - 1, 0);
        allResults[prevIndex]?.focus();
      }
    } else if (e.key === "Escape") {
      e.preventDefault();
      e.stopPropagation();
      setIsOpen(false);
      containerRef.current?.querySelector<HTMLInputElement>("input")?.focus();
    }
  };

  // Render popover in portal for proper z-index stacking
  const popoverContent =
    isOpen && popoverPosition && typeof document !== "undefined"
      ? createPortal(
          <div
            ref={popoverRef}
            style={{
              position: "fixed",
              top: `${popoverPosition.top}px`,
              left: `${popoverPosition.left}px`,
              width: `${popoverPosition.width}px`,
              zIndex: 9999,
            }}
            role="listbox"
            onKeyDown={handlePopoverKeyDown}
          >
            <div className="bg-background-surface shadow-2xl border border-border-default rounded-lg overflow-hidden animate-in fade-in zoom-in-95 duration-200">
              <div className="overflow-y-auto" style={{ maxHeight }}>
                {children}
              </div>
            </div>
          </div>,
          document.body
        )
      : null;

  // Variant-specific className for TextField
  const textFieldClassName =
    variant === "elevated"
      ? "bg-background-muted/50 border-border-subtle/50 focus-within:bg-background-body focus-within:border-border-focus focus-within:shadow-md backdrop-blur-sm transition-all duration-200"
      : "";

  return (
    <div
      key="desktop"
      ref={containerRef}
      className={`relative w-full ${className || ""}`}
      onKeyDown={handleInputKeyDown}
    >
      <TextField
        value={value}
        onChange={handleInputChange}
        placeholder={placeholder}
        showSearchIcon
        isClearable={!!value}
        onClear={() => onChange?.("")}
        reserveErrorSpace={false}
        className={textFieldClassName}
        onFocus={() => setIsOpen(true)}
      />
      {popoverContent}
    </div>
  );
}
